import React, { useState } from 'react'
import Dialog from 'components/Popup/Dialog'
import Loader from 'components/Loader'
import { getMessage } from 'lib/translator'
import {
  BULK_DELETE_MAX_RECORDS,
  BULK_DELETE_PREVIEW_DISPLAY_LIMIT,
  MAX_IMAGE_SIDE,
  MIN_IMAGE_SIDE,
  SAMPLE_BULK_DELETE_CSV,
} from '../constants'
import {
  getBulkDeleteErrorMessage,
  previewBulkDelete,
  submitBulkDelete,
} from '../utils'
import {
  createBulkDeleteCsvFile,
  parseBulkDeleteCsvContent,
  validateBulkDeleteRows,
} from './csvUtils'
import { buildPreviewDisplayItems } from './previewItems'
import CsvDropzone from './CsvDropzone'

function MediaBulkDelete() {
  const [csvFile, setCsvFile] = useState(null)
  const [csvFileName, setCsvFileName] = useState('')
  const [rowCount, setRowCount] = useState(0)
  const [previewItems, setPreviewItems] = useState([])
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [errorDialog, setErrorDialog] = useState(null)
  const [successDialog, setSuccessDialog] = useState(null)
  const [previewPage, setPreviewPage] = useState(0)

  const deleteCount = rowCount || previewItems.length
  const previewPageSize = BULK_DELETE_PREVIEW_DISPLAY_LIMIT
  const previewRowCount = previewItems.length
  const previewPageCount = Math.max(
    1,
    Math.ceil(previewRowCount / previewPageSize)
  )
  const safePreviewPage = Math.min(previewPage, previewPageCount - 1)
  const previewRangeStart =
    previewRowCount === 0 ? 0 : safePreviewPage * previewPageSize + 1
  const previewRangeEnd = Math.min(
    (safePreviewPage + 1) * previewPageSize,
    previewRowCount
  )
  const displayedItems = previewItems.slice(
    safePreviewPage * previewPageSize,
    previewRangeEnd
  )

  const resetState = () => {
    setCsvFile(null)
    setCsvFileName('')
    setRowCount(0)
    setPreviewItems([])
    setPreviewPage(0)
  }

  const showValidationError = (validationResult) => {
    setErrorDialog({
      title: getMessage('catalogue.skuMediaUpload.bulkDelete.validation.title'),
      message: getMessage(
        validationResult.errorKey,
        validationResult.values || {}
      ),
    })
    resetState()
  }

  const loadPreview = async (file, parsedRows) => {
    setLoadingPreview(true)
    setPreviewItems([])
    setPreviewPage(0)

    try {
      const { items } = await previewBulkDelete(file)
      setPreviewItems(buildPreviewDisplayItems(items, parsedRows))
      setPreviewPage(0)
    } catch (error) {
      setErrorDialog({
        title: getMessage(
          'catalogue.skuMediaUpload.bulkDelete.previewError.title'
        ),
        message: getBulkDeleteErrorMessage(error),
      })
      resetState()
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleFileUpload = (file) => {
    if (!file) {
      return
    }

    if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
      setErrorDialog({
        title: getMessage(
          'catalogue.skuMediaUpload.bulkDelete.wrongFormat.title'
        ),
        message: getMessage(
          'catalogue.skuMediaUpload.bulkDelete.wrongFormat.message'
        ),
      })
      return
    }

    resetState()

    const reader = new FileReader()
    reader.onload = async (event) => {
      const { rows, error: parseError } = parseBulkDeleteCsvContent(
        event.target.result
      )

      if (parseError) {
        showValidationError(parseError)
        return
      }

      const validationError = validateBulkDeleteRows(rows)
      if (validationError) {
        showValidationError(validationError)
        return
      }

      const normalizedFile = createBulkDeleteCsvFile(rows, file.name)
      setCsvFile(normalizedFile)
      setCsvFileName(file.name)
      setRowCount(rows.length)
      await loadPreview(normalizedFile, rows)
    }
    reader.readAsText(file)
  }

  const handleConfirmDelete = async () => {
    if (!csvFile || deleting) {
      return
    }

    setDeleting(true)
    try {
      await submitBulkDelete(csvFile)
      setSuccessDialog({
        title: getMessage('catalogue.skuMediaUpload.bulkDelete.success.title'),
        message: getMessage(
          'catalogue.skuMediaUpload.bulkDelete.success.message',
          { count: deleteCount }
        ),
      })
      resetState()
    } catch (error) {
      setErrorDialog({
        title: getMessage('catalogue.skuMediaUpload.bulkDelete.error.title'),
        message: getBulkDeleteErrorMessage(error),
      })
    } finally {
      setDeleting(false)
    }
  }

  const showUploadZone = displayedItems.length === 0 && !loadingPreview

  return (
    <div className="sku-media-tab sku-media-bulk-delete-tab">
      <p className="sku-media-tab-description">
        {getMessage('catalogue.skuMediaUpload.bulkDelete.description', {
          maxRows: BULK_DELETE_MAX_RECORDS,
          previewMax: BULK_DELETE_PREVIEW_DISPLAY_LIMIT,
          minSide: MIN_IMAGE_SIDE,
          maxSide: MAX_IMAGE_SIDE,
        })}
      </p>

      {showUploadZone && (
        <>
          <CsvDropzone onFileSelect={handleFileUpload} disabled={deleting} />
          <div className="sku-media-csv-sample-download text-center">
            <a
              className="download-link"
              href={SAMPLE_BULK_DELETE_CSV}
              download="product-image-delete-sample.csv"
            >
              {getMessage('catalogue.skuMediaUpload.bulkDelete.sampleDownload')}
            </a>
          </div>

          {csvFileName && !loadingPreview && (
            <p
              className="sku-media-selected-file"
              data-testid="sku-media-delete-csv-name"
            >
              {getMessage('catalogue.skuMediaUpload.bulkDelete.selectedFile', {
                fileName: csvFileName,
                count: rowCount,
              })}
            </p>
          )}
        </>
      )}

      {(loadingPreview || deleting) && <Loader />}

      {displayedItems.length > 0 && !loadingPreview && (
        <div
          className="sku-media-delete-preview"
          data-testid="sku-media-delete-preview"
        >
          <h3>
            {getMessage('catalogue.skuMediaUpload.bulkDelete.previewHeading')}
          </h3>

          {previewRowCount > previewPageSize && (
            <div
              className="sku-media-preview-pagination"
              data-testid="sku-media-delete-preview-pagination"
            >
              <button
                type="button"
                className="button secondary"
                disabled={safePreviewPage === 0}
                data-testid="sku-media-delete-preview-prev"
                onClick={() => setPreviewPage((page) => page - 1)}
              >
                {getMessage(
                  'catalogue.skuMediaUpload.bulkDelete.previewPrevious'
                )}
              </button>
              <span className="sku-media-preview-range">
                {getMessage(
                  'catalogue.skuMediaUpload.bulkDelete.previewRange',
                  {
                    start: previewRangeStart,
                    end: previewRangeEnd,
                    total: previewRowCount,
                  }
                )}
              </span>
              <button
                type="button"
                className="button secondary"
                disabled={safePreviewPage >= previewPageCount - 1}
                data-testid="sku-media-delete-preview-next"
                onClick={() => setPreviewPage((page) => page + 1)}
              >
                {getMessage('catalogue.skuMediaUpload.bulkDelete.previewNext')}
              </button>
            </div>
          )}

          <div
            className="sku-media-delete-preview-table-wrap"
            data-testid="sku-media-delete-preview-table-wrap"
          >
            <table className="sku-media-results-table sku-media-delete-preview-table">
              <colgroup>
                <col className="sku-media-preview-col-sku" />
                <col className="sku-media-preview-col-side" />
              </colgroup>
              <thead>
                <tr>
                  <th>{getMessage('catalogue.skuMediaUpload.table.sku')}</th>
                  <th>
                    {getMessage('catalogue.skuMediaUpload.table.imageSide')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedItems.map((item, index) => (
                  <tr key={`${item.sku}-${item.imageSideCode}-${index}`}>
                    <td>{item.sku}</td>
                    <td>{item.sideLabel || item.imageSideCode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sku-media-delete-actions">
            <button
              type="button"
              className="button secondary"
              disabled={deleting}
              data-testid="sku-media-delete-clear"
              onClick={resetState}
            >
              {getMessage('catalogue.skuMediaUpload.bulkDelete.clearButton')}
            </button>
            <button
              type="button"
              className="button primary"
              disabled={deleting || loadingPreview}
              data-testid="sku-media-delete-confirm"
              onClick={handleConfirmDelete}
            >
              {deleting
                ? getMessage('catalogue.skuMediaUpload.bulkDelete.deleting')
                : getMessage(
                    'catalogue.skuMediaUpload.bulkDelete.confirmButton',
                    { count: deleteCount }
                  )}
            </button>
          </div>
        </div>
      )}

      {errorDialog && (
        <Dialog
          className="error"
          show
          title={errorDialog.title}
          information={errorDialog.message}
          close={() => setErrorDialog(null)}
          closeText={getMessage('dialog.okText')}
        />
      )}
      {successDialog && (
        <Dialog
          className="success"
          show
          title={successDialog.title}
          information={successDialog.message}
          close={() => setSuccessDialog(null)}
          closeText={getMessage('dialog.okText')}
        />
      )}
    </div>
  )
}

export default MediaBulkDelete
